import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { usePageMeta } from '@/lib/usePageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/strategy-intensive`;

const STAGES = [
  'Idea / Pre-launch',
  'New Business',
  'Early Growth',
  'Established',
  'Scaling / Expanding',
];

export default function StrategyIntensive() {
  const [config, setConfig] = useState(null);
  const [params] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const state = params.get('state');

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  usePageMeta({
    title: 'Strategy Intensive with Toia Wallace',
    description: 'A private 90-minute strategy session for purpose-driven women founders — clarity, positioning, and a customized action plan for growth. Application-only.',
    path: '/strategy-intensive',
  });

  const price = config?.price_cents ? (config.price_cents / 100).toFixed(0) : '297';

  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section className="pt-32 sm:pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow text-taupe mb-6 sm:mb-8">Private Sessions · Application Only</div>
            <h1 className="font-serif-display text-4xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.94] tracking-tightest text-matte">
              Strategy
              <br />
              <em className="italic text-taupe">Intensive.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 min-w-0">
            <div className="bg-white rounded-2xl border border-taupe/20 p-6 sm:p-8">
              <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-3">One session · ${price}</div>
              <div className="font-serif-display text-3xl sm:text-4xl text-matte leading-tight mb-3">90 minutes with Toia.</div>
              <p className="text-matte/70 text-[14px] leading-relaxed mb-6">
                A focused, high-level strategy experience — not a beginner overview.
              </p>
              <a href="#apply" className="btn-primary w-full justify-center" data-testid="si-hero-apply-cta">
                Apply Now
                <ArrowRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {state === 'payment_cancelled' && (
        <div className="mx-auto max-w-[1100px] px-6 sm:px-10 lg:px-16 mb-8">
          <div className="p-4 bg-[#C8A15A]/10 border border-[#C8A15A]/40 rounded-lg text-[13px] text-matte">
            You cancelled the payment before it completed. Your acceptance is still active — return to your acceptance email to try again when you&rsquo;re ready.
          </div>
        </div>
      )}

      {/* WHAT IT IS */}
      {config && (
        <section className="py-12 sm:py-16 lg:py-24 bg-white">
          <div className="mx-auto max-w-[1100px] px-6 sm:px-10 lg:px-16">
            <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-[1.4] text-matte tracking-tight max-w-3xl">
              {config.tagline}
            </p>
            <p className="mt-6 sm:mt-8 text-matte/70 leading-relaxed max-w-2xl">{config.description}</p>
            {config.positioning_note && (
              <p className="mt-6 italic text-taupe border-l-2 border-gold/40 pl-4 max-w-2xl">
                {config.positioning_note}
              </p>
            )}
          </div>
        </section>
      )}

      {/* DETAILS GRID */}
      {config && (
        <section className="py-12 sm:py-16 lg:py-24">
          <div className="mx-auto max-w-[1100px] px-6 sm:px-10 lg:px-16 grid md:grid-cols-3 gap-6 sm:gap-8">
            <DetailCard title="Who it's for" items={config.who_its_for || []} />
            <DetailCard title="Focus areas" items={config.focus_areas || []} />
            <DetailCard title="You'll leave with" items={config.outcomes || []} />
          </div>
        </section>
      )}

      {/* APPLICATION FORM */}
      <ApplicationForm submitted={submitted} onSubmit={() => setSubmitted(true)} />
    </div>
  );
}

function DetailCard({ title, items }) {
  return (
    <div className="bg-white rounded-2xl border border-taupe/20 p-6 sm:p-8">
      <div className="uppercase tracking-[0.22em] text-[10px] text-taupe mb-4">{title}</div>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-matte/85 text-[14px] leading-snug">
            <CheckCircle2 size={16} strokeWidth={1.4} className="text-gold shrink-0 mt-0.5" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ApplicationForm({ submitted, onSubmit }) {
  const [f, setF] = useState({
    name: '', email: '', phone: '', business_name: '', business_website: '', social_media: '',
    offer_description: '', target_audience: '', business_length: '', business_stage: '',
    biggest_challenge: '', session_goal: '', tried_before: '',
    ready_to_implement: 'yes', additional_notes: '', ack_agreed: false, website: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.ack_agreed) { toast.error('Please acknowledge the application notice.'); return; }
    if (!f.biggest_challenge?.trim()) { toast.error('The biggest challenge field is required.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/apply`, f);
      toast.success('Application received.');
      onSubmit();
      // Scroll to top of the submitted section
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong.');
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <section id="apply" className="py-16 sm:py-24 lg:py-32 bg-matte text-ivory">
        <div className="mx-auto max-w-2xl px-6 text-center" data-testid="si-application-confirmation">
          <div className="eyebrow text-gold mb-6">Received</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-tight">
            Thank you.
          </h2>
          <p className="mt-6 sm:mt-8 text-ivory/70 leading-relaxed max-w-lg mx-auto">
            Your Strategy Intensive application has been received. Every application is personally reviewed to ensure the session is aligned with your current business needs. If accepted, you&rsquo;ll receive instructions to secure your session with the $297 payment and schedule your 90-minute Strategy Intensive.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="apply" className="py-12 sm:py-16 lg:py-24 bg-matte text-ivory">
      <div className="mx-auto max-w-[820px] px-6 sm:px-10">
        <div className="eyebrow text-gold mb-6">Apply · Strategy Intensive</div>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
          Tell me about your business.
        </h2>
        <p className="mt-4 text-ivory/70 max-w-lg">
          Applications are personally reviewed. Not all applicants are accepted — this ensures the session is a fit for both of us.
        </p>

        <form onSubmit={submit} className="mt-8 sm:mt-10 space-y-5" data-testid="si-application-form">
          <input type="text" name="website" value={f.website} onChange={set('website')} tabIndex={-1} autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} />

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name" value={f.name} onChange={set('name')} required testId="si-field-name" />
            <Field label="Email" type="email" value={f.email} onChange={set('email')} required testId="si-field-email" />
            <Field label="Phone (optional)" value={f.phone} onChange={set('phone')} />
            <Field label="Business Name" value={f.business_name} onChange={set('business_name')} />
            <Field label="Business Website" value={f.business_website} onChange={set('business_website')} placeholder="https://" />
            <Field label="Social Media / Instagram" value={f.social_media} onChange={set('social_media')} placeholder="@handle or URL" />
          </div>

          <TextArea label="What does your business currently offer?" value={f.offer_description} onChange={set('offer_description')} rows={3} />
          <TextArea label="Who do you currently serve?" value={f.target_audience} onChange={set('target_audience')} rows={2} />

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="How long have you been in business?" value={f.business_length} onChange={set('business_length')} />
            <Select label="What stage best describes your business?" value={f.business_stage} onChange={set('business_stage')} options={STAGES} />
          </div>

          <TextArea label="What is the biggest challenge or area of confusion in your business right now? *" value={f.biggest_challenge} onChange={set('biggest_challenge')} rows={4} required testId="si-field-challenge" />
          <TextArea label="What would you most like to accomplish during your Strategy Intensive?" value={f.session_goal} onChange={set('session_goal')} rows={3} />
          <TextArea label="What have you already tried to address this challenge?" value={f.tried_before} onChange={set('tried_before')} rows={3} />

          <div>
            <label className="uppercase tracking-[0.22em] text-[10px] text-ivory/70 mb-2 block">Are you prepared to implement the recommendations and action steps discussed during your session?</label>
            <div className="flex flex-wrap gap-3">
              {[['yes', 'Yes'], ['no', 'No'], ['unsure', "I'm not sure"]].map(([v, label]) => (
                <label key={v} className={`cursor-pointer px-4 py-2 rounded-full border text-[12px] uppercase tracking-[0.18em] transition-colors ${f.ready_to_implement === v ? 'bg-gold text-matte border-gold' : 'border-ivory/30 text-ivory/80 hover:border-gold hover:text-gold'}`}>
                  <input type="radio" name="ready" value={v} checked={f.ready_to_implement === v} onChange={set('ready_to_implement')} className="sr-only" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <TextArea label="Is there anything else Toia should know when reviewing your application?" value={f.additional_notes} onChange={set('additional_notes')} rows={3} />

          <div className="p-4 sm:p-5 bg-ivory/5 border border-ivory/15 rounded-lg">
            <p className="text-ivory/80 text-[13px] leading-relaxed mb-4">
              Submitting an application does not guarantee acceptance into a Strategy Intensive. Applications are reviewed to ensure the session is aligned with your current business needs. If accepted, you will receive instructions to secure your session with the $297 payment and schedule your 90-minute Strategy Intensive.
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={f.ack_agreed}
                onChange={(e) => setF((p) => ({ ...p, ack_agreed: e.target.checked }))}
                data-testid="si-field-ack"
                className="mt-1 w-4 h-4 accent-gold"
              />
              <span className="text-ivory/85 text-[13px]">I understand that submission does not guarantee acceptance or reserve a session.</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="si-application-submit"
            className="btn-primary bg-gold text-matte hover:bg-[#b8944e] w-full sm:w-auto disabled:opacity-60"
          >
            {busy ? 'Submitting…' : 'Submit Application'}
            <ArrowRight size={14} strokeWidth={1.6} />
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', required, testId, placeholder }) {
  return (
    <label className="block">
      <div className="uppercase tracking-[0.22em] text-[10px] text-ivory/70 mb-2">{label}</div>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b border-ivory/25 text-ivory py-2 focus:outline-none focus:border-gold text-[15px]"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 3, required, testId }) {
  return (
    <label className="block">
      <div className="uppercase tracking-[0.22em] text-[10px] text-ivory/70 mb-2">{label}</div>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        required={required}
        data-testid={testId}
        className="w-full bg-transparent border-b border-ivory/25 text-ivory py-2 focus:outline-none focus:border-gold text-[15px] resize-y"
      />
    </label>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="uppercase tracking-[0.22em] text-[10px] text-ivory/70 mb-2">{label}</div>
      <select value={value} onChange={onChange} className="w-full bg-transparent border-b border-ivory/25 text-ivory py-2 focus:outline-none focus:border-gold text-[15px]">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o} className="bg-matte">{o}</option>)}
      </select>
    </label>
  );
}
